/* eslint-disable complexity */
import { isEmpty } from 'lodash';
import { InputFieldConfig } from 'src/@vymo/ui/molecules/form/types';
import { createSelector } from '@reduxjs/toolkit';
import { ACTIVITY } from 'src/models/lmsConfig/constants';
import { getRevyreConfig } from 'src/models/lmsConfig/selectors';
import { SUPERVISOR_REVIEW_DOMAINTYPE } from 'src/modules/constants';
import { RootState } from 'src/store';
import { DISPOSITION_GROUP } from '../constants';
import { getAllowedActions } from '../details/selectors';
import { getDomainConfigByType } from '../selectors';
import { DomainType, ScenarioType, WorkflowType } from '../types';
import { isActivtiyCompletedFieldConfig } from './constants';
import {
  applyEntitySelectionRules,
  getActivityFields,
  getActivityInputsByCompletion,
  getActivityUIFieldsGrouping,
  getFilteredAllowedActionsForActivityUpdate,
  getInputsAndGroups,
  getUIFieldsGrouping,
  getUpdateFields,
} from './queries';
import { RevyreFormProps } from './types';

export const getOutcomeValue = (
  formData: Record<string, { value; type }>,
  type: ScenarioType,
) => {
  const isCompleted = formData?.isTheActivityCompleted?.value?.toUpperCase();
  if (isCompleted === 'NO' && type !== 'update') return undefined;

  return Object.values(formData).find(
    (field) => field?.type === 'entity_next_event',
  )?.value;
};

export const getActivityTypesForWorkflow = (
  state: RootState,
  workflowType: WorkflowType,
) =>
  getDomainConfigByType(state, 'workflow', workflowType)?.activityConfig || [];

export const getWorkflowsWithActivitiesTypes = createSelector(
  [(state) => getRevyreConfig(state)],
  (revyreConfig) =>
    Object.values(revyreConfig)
      .map((workflowConfigByType: any) => ({
        ...workflowConfigByType,
        code: workflowConfigByType?.code,
        options:
          workflowConfigByType?.activityConfig?.map((activity) => ({
            ...activity,
            id: `${workflowConfigByType?.code}__${activity?.code}`,
          })) ?? [],
      }))
      .filter((workflow) => workflow.options.length > 0),
);

// eslint-disable-next-line max-lines-per-function
export const getStandardInputsAndGroups = (
  type: RevyreFormProps['type'],
  domainType: DomainType,
  entityId: string,
  formData: Record<
    string,
    {
      additionalData?;
      value;
      type;
    }
  >,
  inputsMap: Record<string, any>,
  metaData: Record<string, any>,
) => {
  const {
    workflowType,
    eventId,
    activityType,
    isBlockingScreen,
    isPostCallForm,
    buttonMetadata,
  } = metaData;

  return createSelector(
    [
      getWorkflowsWithActivitiesTypes,
      (state: RootState) => getAllowedActions(state, entityId, domainType),
      () => inputsMap,
      (state) => getRevyreConfig(state),
    ],
    // eslint-disable-next-line complexity
    // eslint-disable-next-line max-lines-per-function
    (
      workflowsWithActivitiesTypes,
      allowedActions,
      initalDataMap,
      revyreConfig,
    ) => {
      let result: { inputs: any[]; groups: any[] } = { inputs: [], groups: [] };
      const activityTypesForWorkflow =
        revyreConfig?.[workflowType]?.activityConfig;
      const activityConfig = revyreConfig?.[ACTIVITY];

      switch (domainType) {
        case 'activity': {
          let inputs: any[] = [];
          let initialUiFields: InputFieldConfig[] = [];
          let onActivitySelection: InputFieldConfig[] = [];

          if (['create', 'detailsCreate', 'createGlobal'].includes(type)) {
            const activityCompletionStatus =
              formData?.isTheActivityCompleted?.additionalData?.data?.code;

            const isActivityCompletedValue = activityCompletionStatus
              ? activityCompletionStatus === 'YES'
              : undefined;

            initialUiFields = [
              ...getActivityFields(
                type,
                activityTypesForWorkflow,
                workflowType,
                workflowsWithActivitiesTypes,
              ),
              isActivtiyCompletedFieldConfig,
            ];

            onActivitySelection = getActivityInputsByCompletion(
              activityTypesForWorkflow,
              isActivityCompletedValue,
              activityType,
              formData,
            );

            inputs = [...initialUiFields, ...onActivitySelection];
          }

          if (type === 'update') {
            const filteredAllowedActions =
              getFilteredAllowedActionsForActivityUpdate(allowedActions);
            const stateOptions =
              isBlockingScreen || isPostCallForm
                ? filteredAllowedActions.filter(
                    (item) => item.allowedAction === 'complete',
                  )
                : filteredAllowedActions;

            initialUiFields = [
              ...getUpdateFields(stateOptions),
              ...getActivityFields(
                type,
                activityTypesForWorkflow,
                workflowType,
                workflowsWithActivitiesTypes,
              ),
            ];
            onActivitySelection =
              eventId === 'complete'
                ? activityTypesForWorkflow?.find(
                    (activity) =>
                      activity.code === activityType ||
                      activity.name === activityType,
                    // @ts-ignore
                  )?.complete_inputs ?? []
                : activityConfig?.events?.find(
                    (event) => event.allowedAction === eventId,
                  )?.config?.inputs || [];

            inputs = [...initialUiFields, ...onActivitySelection];
          }

          result = {
            inputs,
            groups: getActivityUIFieldsGrouping({
              initialUiFields,
              onActivitySelection,
            }),
          };
          break;
        }

        case 'payment': {
          if (type === 'update' && isEmpty(buttonMetadata)) {
            const inputs = getUpdateFields(allowedActions);
            result = {
              inputs,
              groups: getUIFieldsGrouping(inputs),
            };
          } else {
            result = { inputs: [], groups: [] };
          }
          break;
        }

        default: {
          if (type === 'update') {
            const inputs = getUpdateFields(allowedActions);
            result = {
              inputs,
              groups: getUIFieldsGrouping(inputs),
            };
          } else {
            result = { inputs: [], groups: [] };
          }
          break;
        }
      }

      return applyEntitySelectionRules(
        result.inputs,
        result.groups,
        initalDataMap,
        entityId,
        type,
        domainType,
      );
    },
  );
};

// eslint-disable-next-line max-lines-per-function
export const getFormInputsAndGroupsByEventId = (
  type: RevyreFormProps['type'],
  domainType: DomainType,
  formData: Record<string, { value; type }>,
  inputsMap: Record<string, any>,
  metaData: Record<string, any>,
) => {
  const { eventId, outcomeId, buttonMetadata, standardInputs } = metaData;

  let { workflowType } = metaData;
  const { parentAssociateWorkflowType } = buttonMetadata || {};

  if (parentAssociateWorkflowType) {
    workflowType = parentAssociateWorkflowType;
  }

  return createSelector(
    [
      (state: RootState) =>
        getDomainConfigByType(state, domainType, workflowType),
      (state: RootState) =>
        getDomainConfigByType(state, 'workflow', workflowType),
      () => inputsMap,
      () => standardInputs,
    ],
    (domainConfig, workflowConfig, initalDataMap, currentStandardInputs) => {
      if (parentAssociateWorkflowType) {
        domainType = 'workflow';
      }

      let result = { inputs: [], groups: [] };
      switch (domainType) {
        case 'activity':
          if (
            type === 'create' ||
            type === 'createGlobal' ||
            type === 'detailsCreate' ||
            type === 'update'
          ) {
            const isOutcomeGroupFieldPresent = currentStandardInputs?.some(
              (input: InputFieldConfig) => input?.code === DISPOSITION_GROUP,
            );
            if (!isOutcomeGroupFieldPresent) {
              result = { inputs: [], groups: [] };
              break;
            }

            result = getInputsAndGroups(workflowConfig?.events, outcomeId) as {
              inputs: [];
              groups: [];
            };
          }
          break;

        case SUPERVISOR_REVIEW_DOMAINTYPE:
        case 'payment': {
          if (['detailsCreate', 'createGlobal', 'create'].includes(type)) {
            result = getInputsAndGroups(domainConfig?.events, 'create') as {
              inputs: [];
              groups: [];
            };
          } else if (type === 'update') {
            result = getInputsAndGroups(domainConfig?.events, eventId) as {
              inputs: [];
              groups: [];
            };
          }

          break;
        }

        default:
          if (['detailsCreate', 'createGlobal', 'create'].includes(type)) {
            result = getInputsAndGroups(workflowConfig?.events, 'create') as {
              inputs: [];
              groups: [];
            };
          } else if (type === 'update') {
            result = getInputsAndGroups(workflowConfig?.events, eventId) as {
              inputs: [];
              groups: [];
            };
          }
          break;
      }
      return applyEntitySelectionRules(
        result?.inputs,
        result?.groups,
        initalDataMap,
        undefined,
        type,
        domainType,
      );
    },
  );
};
